'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { useLocaleTranslation } from '@/hooks/use-locale-translation';
import styles from './home-live.module.css';
import { liveEventHref } from './company-href';
import { Odometer } from './Odometer';
import {
  startLiveMap,
  type FeedEvent,
  type LiveEvent,
  type LiveMapData,
} from './live-map-engine';
import type { LiveFeedResponse } from '@/lib/backend-api';

const MAP_DATA_URL = '/home-v2/live-map.json';
const FONT = 'Inter, system-ui, -apple-system, sans-serif';
// The canvas cannot read CSS variables: .fd-home's --fd-ink and --fd-bg.
const INK = '#0e0e0c';
const PAPER = '#fafaf8';

const FEED_URL = '/api/live-feed';
const FEED_POLL_MS = 60_000;
// Only the newest entries: the feed also carries new companies discovered
// hours ago, which would otherwise open the replay.
const FEED_ENTRIES = 20;

// German time for everyone, not the visitor's clock.
const BERLIN_TIME = new Intl.DateTimeFormat('de-DE', {
  timeZone: 'Europe/Berlin', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
});
const BERLIN_DAY = new Intl.DateTimeFormat('de-DE', {
  timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit',
});

// Illustrative values for the fallback simulation only (feed unreachable).
const DEMO_COUNTS = { companies: 2412689, financials: 13874212, updated: 38412 };

type Counts = { companies: number | null; financials: number | null; updated: number | null };

function toFeedEvent(i: LiveFeedResponse['items'][number]): FeedEvent {
  return {
    name: i.name,
    city: i.city ?? '',
    registerCourt: i.register_court ?? '',
    registerType: i.register_type ?? '',
    registerId: i.register_id ?? '',
    key: i.kind,
    at: Date.parse(i.at),
    postcode: i.postcode,
  };
}


export function LiveSection({
  locale,
  onEvent,
  isHeroVisible,
  companyCount,
}: {
  locale: string;
  onEvent: (e: LiveEvent) => void;
  isHeroVisible: () => boolean;
  companyCount: number | null;
}) {
  const { t, locale: lang } = useLocaleTranslation();
  const mapRef = useRef<HTMLDivElement>(null);
  const baseRef = useRef<HTMLCanvasElement>(null);
  const fxRef = useRef<HTMLCanvasElement>(null);
  const [data, setData] = useState<LiveMapData | null>(null);
  const [events, setEvents] = useState<LiveEvent[]>([]);
  const [counts, setCounts] = useState<Counts>(() => ({
    companies: companyCount, financials: null, updated: null,
  }));
  // 'live': real arrivals from /api/live-feed, replayed oldest first a few
  // seconds apart, then new ones as polling brings them; nothing invented.
  // 'demo': the feed could not be loaded, so the illustrative simulation runs.
  const [mode, setMode] = useState<'loading' | 'live' | 'demo'>('loading');
  const queueRef = useRef<FeedEvent[]>([]);
  const newestRef = useRef(0);
  // The map engine keeps running across renders; it reads the latest callbacks.
  const onEventRef = useRef(onEvent);
  const heroVisibleRef = useRef(isHeroVisible);
  useEffect(() => {
    onEventRef.current = onEvent;
    heroVisibleRef.current = isHeroVisible;
  });

  useEffect(() => {
    let cancelled = false;
    fetch(MAP_DATA_URL)
      .then((r) => (r.ok ? (r.json() as Promise<LiveMapData>) : null))
      .then((d) => {
        if (!cancelled && d) setData(d);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Poll the feed: queue arrivals newer than any seen, take the counters.
  useEffect(() => {
    let cancelled = false;
    let timer = 0;
    const poll = async () => {
      try {
        const res = await fetch(FEED_URL);
        if (!res.ok) throw new Error(String(res.status));
        const feed = (await res.json()) as LiveFeedResponse;
        if (cancelled) return;
        const fresh = feed.items.map(toFeedEvent)
          .filter((e) => Number.isFinite(e.at))
          .sort((a, b) => b.at - a.at)
          .slice(0, FEED_ENTRIES)
          .filter((e) => e.at > newestRef.current)
          .reverse();
        if (fresh.length) {
          queueRef.current.push(...fresh);
          newestRef.current = fresh[fresh.length - 1].at;
        }
        setCounts({
          companies: feed.companies_estimate || companyCount,
          financials: feed.financials_estimate || null,
          updated: feed.arrivals_24h,
        });
        setMode((m) => (m === 'demo' ? m : 'live'));
      } catch {
        if (!cancelled) setMode((m) => (m === 'loading' ? 'demo' : m));
      }
      if (!cancelled) timer = window.setTimeout(poll, FEED_POLL_MS);
    };
    poll();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [companyCount]);

  useEffect(() => {
    if (mode === 'demo') setCounts((c) => ({ ...DEMO_COUNTS, companies: c.companies ?? DEMO_COUNTS.companies }));
  }, [mode]);

  const handleEvent = useCallback((e: LiveEvent) => {
    setEvents((prev) => [e, ...prev].slice(0, 3));
    onEventRef.current(e);
  }, []);

  useEffect(() => {
    const mapEl = mapRef.current;
    const base = baseRef.current;
    const fx = fxRef.current;
    if (!data || !mapEl || !base || !fx || mode === 'loading') return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const handle = startLiveMap({
      mapEl,
      base,
      fx,
      data,
      reduce,
      font: FONT,
      ink: INK,
      paper: PAPER,
      onEvent: handleEvent,
      isHeroVisible: () => heroVisibleRef.current(),
      nextEvent: mode === 'live' ? () => queueRef.current.shift() ?? null : undefined,
    });
    let rt = 0;
    const onResize = () => {
      window.clearTimeout(rt);
      rt = window.setTimeout(() => handle.resize(), 120);
    };
    window.addEventListener('resize', onResize);
    return () => {
      handle.destroy();
      window.removeEventListener('resize', onResize);
      window.clearTimeout(rt);
    };
  }, [data, handleEvent, mode]);

  const lagFmt = (s: number) =>
    new Intl.NumberFormat(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(s);
  // Today's arrivals by German time; an older one carries its date.
  const timeFmt = (ms: number) => {
    const time = BERLIN_TIME.format(ms);
    const day = BERLIN_DAY.format(ms);
    return day === BERLIN_DAY.format(Date.now()) ? time : `${day} ${time}`;
  };
  const live = mode !== 'demo';
  const tickerHead = live ? (
    <span>{t('homeV2.live.received')}</span>
  ) : (
    <>
      <span>{t('homeV2.live.published')}</span>
      <span>{t('homeV2.live.delay')}</span>
    </>
  );

  return (
    <section className={`${styles.section} ${styles.pad}`} id="live">
      <div className={`${styles.split} ${styles.head}`}>
        <h2 className={styles.sqLabel}>{t('homeV2.live.label')}</h2>
        <div className={`${styles.tickerH} ${styles.wide}`} aria-hidden="true">
          {tickerHead}
        </div>
      </div>
      <div className={styles.liveGrid}>
        <div>
          <div
            ref={mapRef}
            className={styles.map}
            role="img"
            aria-label={t('homeV2.live.mapAria')}
            style={data ? ({ '--aspect': data.aspect } as React.CSSProperties) : undefined}
          >
            <canvas ref={baseRef} />
            <canvas ref={fxRef} />
          </div>
        </div>
        <div className={styles.side}>
          <div className={styles.ticker}>
            <div className={`${styles.tickerH} ${styles.narrow}`}>
              {tickerHead}
            </div>
            <ol aria-live="off">
              {events.map((e) => (
                <li key={e.id} className={e.animate ? styles.isNew : undefined}>
                  <span className={styles.tTime}>{timeFmt(e.at)}</span>
                  {e.lag !== undefined && <span className={styles.tLag}>{lagFmt(e.lag)} s</span>}
                  <Link className={styles.tName} href={liveEventHref(e, lang)}>{e.name}</Link>
                  <span className={styles.tEvent}>{t(`homeV2.events.${e.key}`)}</span>
                </li>
              ))}
            </ol>
            {/* Only the illustrative fallback needs saying so. */}
            {!live && <div className={styles.tickerFoot}>{t('homeV2.live.note')}</div>}
          </div>
          <div className={styles.counts}>
            <div>
              <div className={styles.countK}><b />{t('homeV2.live.companies')}</div>
              <span className={styles.countV}><Odometer value={counts.companies} locale={locale} /></span>
            </div>
            <div>
              <div className={styles.countK}><b />{t('homeV2.live.financials')}</div>
              <span className={styles.countV}><Odometer value={counts.financials} locale={locale} /></span>
            </div>
            <div>
              <div className={styles.countK}><b />{t('homeV2.live.updated')}</div>
              <span className={styles.countV}><Odometer value={counts.updated} locale={locale} /></span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
